// ─────────────────────────────────────────────────────────────────────────────
// nodeCoreDownload.js — the two "save this to disk" actions, and nothing else.
//
// Both screens that offer the Node Core download (the Add Node wizard's Ready
// step and the per-row Download Package modal) use these, so the browser
// awkwardness below is written down once.
//
// Replaced ./agentPackage, which was a hardcoded mock of a package that no
// endpoint served. Both artifacts are real now:
//   GET  /enterprise-nodes/:id/binary-url  → a presigned S3 URL for the binary
//   POST /enterprise-nodes/:id/config      → the generated node-core.json
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Human-readable size. The backend reports `size_bytes` from an S3 HEAD and it is
 * null whenever that call fails, which is a normal, non-fatal outcome — so this
 * has to render something sane for null rather than "NaN MB".
 */
export function formatBytes(bytes) {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

/**
 * Send the browser to the presigned S3 URL to fetch the binary.
 *
 * CALL THIS SYNCHRONOUSLY FROM A CLICK HANDLER. It navigates, and browsers only
 * permit that while the click's transient user activation is live; an `await`
 * beforehand can consume it, after which Chrome drops the navigation silently —
 * no download, no console error, an apparently dead button. useNodeCoreDownload
 * is built around that constraint.
 *
 * WHY NOT fetch()+Blob, which would give a nicer filename: the S3 URL is
 * cross-origin, so pulling it through JS needs CORS on the bucket AND buffers the
 * whole binary (tens of MB) into a tab's memory before writing anything. Handing
 * the URL to the browser instead streams straight to disk with no CORS
 * requirement and no memory cost.
 *
 * The `download` attribute is set but the browser IGNORES it cross-origin — the
 * filename therefore comes from S3's key/headers, not from us. That is a
 * deliberate accepted trade, not an oversight.
 *
 * NO `target="_blank"`, DELIBERATELY. Opening the download in a new tab looks
 * tidier and protects the app from S3's XML error page, but it turns the download
 * into a popup — and popup-blocker extensions silently swallow it. That was
 * observed in the field: the extension returned a STUB window object (every
 * method replaced with `()=>!0`, `document` a bare `{location}`) and recorded the
 * S3 URL under an opener named `pp_pending_ntf:<base64>`, so nothing downloaded
 * and nothing was logged. A same-tab anchor is not treated as a popup.
 *
 * Staying in the same tab is safe for the success case: a response the browser
 * downloads does not navigate the page at all. The cost is the failure case — if
 * S3 refuses, its XML error page replaces the app — which is why the caller
 * checks the URL's remaining lifetime before reusing one, and why the IAM grant
 * being in place matters.
 */
export function downloadBinary(binaryUrl, suggestedName = "node-core") {
  if (!binaryUrl) return;
  const a = document.createElement("a");
  a.href = binaryUrl;
  a.download = suggestedName;   // honoured same-origin only; see above
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Save the config object to disk as node-core.json.
 *
 * `config` is written VERBATIM — it is the file the binary parses, and the
 * server owns its shape (node_id, enrollment_token, gateway_url). Never merge the
 * response's UI metadata (expires_at, token_reused, warnings) into it.
 *
 * Same-origin blob, so the filename IS honoured here, unlike the binary above.
 * The object URL is revoked on a later tick: revoking synchronously after click()
 * races the download in some browsers and yields an empty file.
 *
 * This file contains a live enrollment credential. It is written to the user's
 * disk at their request and deliberately never cached, stored or logged here.
 */
export function downloadConfig(config, filename = "node-core.json") {
  if (!config) return;
  const blob = new Blob([`${JSON.stringify(config, null, 2)}\n`], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/**
 * The command the operator runs once both files are on the host.
 *
 * Single source of truth for this string — it was previously written out by hand
 * in the wizard as a `pq-agent install …` line that named a binary that does not
 * exist and flags node-core does not take.
 */
export function startCommand(filename = "node-core.json") {
  return `./node-core start --config ./${filename}`;
}
